/**
 * The Market screen's You block (docs/market-ux.md §5: position per side, "if settled now",
 * resting orders with Cancel, the Market making quote on this market) and the Venues table (§6:
 * best price per side, size at best, maker/taker fee ≈, limit support, connected).
 */
import { Fragment, useMemo } from "react";
import { Link } from "react-router-dom";
import { bestQuote } from "../../lib/marketsBoard.js";
import { ifSettledNow, marketHolding, venueRows } from "../../lib/marketView.js";
import { fmtPrice, fmtSize } from "../../lib/format.js";
import { useOddsFormat } from "../../lib/odds.js";
import { fillPct, type Ccy } from "../../lib/portfolio.js";
import { KPI_LABEL } from "../../lib/marketMaking.js";
import { SCOPES } from "../../lib/omauth.js";
import { Pill, Skeleton } from "../kit.js";
import { VenueDot } from "../DepthPanel.js";
import { btnBase, btnDanger, fmtContracts, money, signedMoney, toneOf } from "../marketMaking/shared.js";
import { KpiChip, MmKpi } from "../marketMaking/MmKpi.js";
import { mmFocusHref } from "../../pages/MarketMaking.js";
import { useVenueColors } from "./OrderBookBlock.js";
import { BlockCard, type MarketCtx } from "./MarketScreen.js";

/* ======================================================================== You */

export function YouBlock({ ctx }: { ctx: MarketCtx }) {
  const { format } = useOddsFormat();
  const scope = ctx.source.useScope();
  const ccy: Ccy = scope.mode === "real" ? "USD" : "ATLAS";
  const authed = ctx.source.useAccess(SCOPES.account).ok;
  const mine = ctx.source.useMyOrders(ccy);
  const cancel = ctx.source.useCancel();
  const hashes = useMemo(() => ctx.sides.map((s) => s.positionHash), [ctx.sides]);
  const h = useMemo(() => marketHolding(mine.items, hashes), [mine.items, hashes]);
  const quote = ctx.source.useMmQuote(ctx.contestId, ctx.market.marketKey, ctx.book.key, ctx.live);
  const best = ctx.sides.map((s, i) => bestQuote(ctx.live[i] ?? s.position, ctx.allowed)?.price ?? null);
  const now = ifSettledNow(h, best);
  const held = h.sides.some((s) => s.contracts > 0);
  const title = <>You · {ccy === "USD" ? "Real" : "Paper"}</>;

  if (!authed) {
    return (
      <BlockCard title="You">
        <p className="text-[13px] text-dim">No position in this market</p>
      </BlockCard>
    );
  }
  if (mine.isLoading) {
    return (
      <BlockCard title={title}>
        <Skeleton className="h-16 w-full" />
      </BlockCard>
    );
  }
  if (!held && h.resting.length === 0 && !quote) {
    return (
      <BlockCard title={title}>
        <p className="text-[13px] text-dim">No position in this market</p>
      </BlockCard>
    );
  }

  return (
    <BlockCard title={title}>
      {held && (
        <>
          <div className="grid grid-cols-[minmax(0,1fr)_repeat(4,auto)] gap-x-3 gap-y-1 text-right text-[12px]">
            <span className="text-left text-[10px] uppercase tracking-wide text-faint">Side</span>
            <span className="text-[10px] uppercase tracking-wide text-faint">Contracts</span>
            <span className="text-[10px] uppercase tracking-wide text-faint">Avg</span>
            <span className="text-[10px] uppercase tracking-wide text-faint">Cost</span>
            <span className="text-[10px] uppercase tracking-wide text-faint" title="Net result if this side wins">If wins</span>
            {ctx.sides.map((s, i) => (
              <Fragment key={s.positionHash}>
                <span className="min-w-0 truncate text-left text-[13px] text-ink">{s.label}</span>
                <span className="nums text-ink">{fmtContracts(h.sides[i].contracts)}</span>
                <span className="nums text-dim">{h.sides[i].avg != null ? fmtPrice(h.sides[i].avg, null, format) : "—"}</span>
                <span className="nums text-dim">{h.sides[i].cost > 0 ? money(h.sides[i].cost, ccy) : "—"}</span>
                <span className={`nums font-medium ${toneOf(h.ifWins[i])}`}>{signedMoney(h.ifWins[i], ccy)}</span>
              </Fragment>
            ))}
          </div>
          <div className="flex items-baseline gap-2 border-t border-border/70 pt-2 text-[12px]">
            <span className="text-dim">If settled now</span>
            <span className="text-[10px] text-faint">est.</span>
            <span className={`nums ml-auto text-[15px] font-semibold ${now ? toneOf(now.pnl) : "text-faint"}`} title={now ? `Worth ${money(now.value, ccy)} at the best prices now, cost ${money(h.cost, ccy)}` : "No price on a side you hold"}>
              {now ? signedMoney(now.pnl, ccy) : "—"}
            </span>
          </div>
        </>
      )}

      {h.resting.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-faint">Resting · {h.resting.length}</span>
          <ul className="flex flex-col divide-y divide-border/50 overflow-hidden rounded-md border border-border/70 bg-bg">
            {h.resting.map((o) => {
              const price = o.price ?? 0;
              const total = price > 0 ? (o.amount ?? 0) / price : 0;
              const filled = price > 0 ? (o.filled_amt ?? 0) / price : 0;
              const col = ctx.sides.findIndex((s) => (o.liquidity_hash ?? "").endsWith(s.positionHash));
              return (
                <li key={o.router_order_id} className="flex min-h-11 items-center gap-2 px-2 text-[12px]">
                  <span className="min-w-0 flex-1 truncate text-ink">{ctx.sides[col]?.label ?? o.title ?? "—"}</span>
                  <span className="nums text-ink">{fmtPrice(o.price, null, format)}</span>
                  <span className="nums text-dim" title={`${fillPct(o).toFixed(0)}% filled`}>
                    {fmtContracts(filled)}/{fmtContracts(total)}
                  </span>
                  <span className="hidden min-w-0 truncate text-faint sm:inline">{o.partner?.partner_name ?? o.partner_id}</span>
                  <button type="button" disabled={cancel.busy} onClick={() => void cancel.cancel([o.router_order_id])} className={`${btnBase} ${btnDanger} !px-2.5`}>
                    Cancel
                  </button>
                </li>
              );
            })}
          </ul>
          {cancel.error && <p className="text-[12px] text-down">{cancel.error}</p>}
        </div>
      )}

      {quote && (
        <div className="flex flex-col gap-1.5 border-t border-border/70 pt-2">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wide text-faint">Market making</span>
            <KpiChip state={quote.state} value={quote.value}>
              {KPI_LABEL[quote.state]}
            </KpiChip>
            <Link to={mmFocusHref(quote.entry.opportunityId)} className="ml-auto inline-flex min-h-11 items-center text-[12px] text-dim hover:text-amber lg:min-h-8">
              Open card ›
            </Link>
          </div>
          <MmKpi state={quote.state} value={quote.value} ccy={quote.entry.ccy} caption={quote.caption} />
        </div>
      )}
    </BlockCard>
  );
}

/* ===================================================================== Venues */

export function VenuesBlock({ ctx }: { ctx: MarketCtx }) {
  const { format } = useOddsFormat();
  const scope = ctx.source.useScope();
  const colors = useVenueColors(ctx);
  // Real: only the connected venues are in scope; the table still lists them all, marked.
  const rows = useMemo(() => venueRows(ctx.book, ctx.side, scope.catalog, scope.connected), [ctx.book, ctx.side, scope.catalog, scope.connected]);
  const pct = (r: number | null) => (r == null ? "—" : `${(Math.round(r * 1000) / 10).toString()}%`);
  return (
    <BlockCard
      title="Venues"
      right={
        <>
          <span className="text-[11px] text-faint">sort</span>
          {ctx.sides.map((s, i) => (
            <Pill key={s.positionHash} active={ctx.side === i} onClick={() => ctx.setSide(i)} className="max-w-[9rem]">
              <span className="block truncate">{s.label}</span>
            </Pill>
          ))}
        </>
      }
    >
      {rows.length === 0 ? (
        <p className="text-[13px] text-dim">No venue is quoting this market</p>
      ) : (
        <div className="overflow-hidden rounded-md border border-border/70 bg-bg">
          <div className="grid grid-cols-[minmax(0,1fr)_4.25rem_4.25rem] gap-x-2 border-b border-border/70 px-2 py-1 text-[10px] uppercase tracking-wide text-faint">
            <span>Venue</span>
            {ctx.sides.map((s) => (
              <span key={s.positionHash} className="truncate text-right" title={s.label}>
                {s.label}
              </span>
            ))}
          </div>
          <ul className="flex flex-col divide-y divide-border/50">
            {rows.map((r) => {
              const out = ctx.allowed != null && !ctx.allowed.has(r.id);
              return (
                <li key={r.id} className={`px-2 py-1.5 ${out ? "opacity-50" : ""}`}>
                  <div className="grid grid-cols-[minmax(0,1fr)_4.25rem_4.25rem] items-center gap-x-2 text-[13px]">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <VenueDot color={colors.get(r.id)} size={7} />
                      <span className="min-w-0 truncate text-ink">{r.name}</span>
                    </span>
                    {r.prices.slice(0, 2).map((p, i) => (
                      <span key={i} className={`nums rounded px-1 text-right ${r.best[i] ? "bg-up/10 font-semibold text-up" : "text-dim"}`}>
                        {p == null ? "—" : fmtPrice(p, r.american[i], format)}
                      </span>
                    ))}
                  </div>
                  <div className="nums mt-0.5 flex flex-wrap gap-x-2.5 text-[11px] text-faint">
                    <span title="Size at the best price on the sorted side">size {fmtSize(r.sizes[ctx.side])}</span>
                    <span title="Maker / taker fee, from the venue's fee_packet (≈ % of stake)">
                      fee ≈ {pct(r.maker)} / {pct(r.taker)}
                    </span>
                    <span>{r.limit == null ? "limit ?" : r.limit ? "limit ✓" : "market only"}</span>
                    {r.connected != null && <span className={r.connected ? "text-up" : ""}>{r.connected ? "connected" : "not connected"}</span>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </BlockCard>
  );
}
